/**
 * Smile cart drawer — bundled section updates, debounced qty, shimmer.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 280;
  var BUNDLE_SECTION = 'api-smile-cart-bundle';
  var RECOMMENDATIONS_SECTION = 'api-smile-cart-recommendations';
  var lastRecommendationsProductId = 0;
  var recommendationsRequestId = 0;

  var rootUrl = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
  var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';

  function getMoneyFormat() {
    var drawer = getDrawer();
    if (drawer && drawer.dataset.moneyFormat) return drawer.dataset.moneyFormat;
    if (window.theme && window.theme.moneyFormat) return window.theme.moneyFormat;
    return '{{amount}}';
  }

  function formatMoneyPipeline(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (isNaN(cents)) return '';

    format = format || getMoneyFormat();
    var token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!token) return (cents / 100).toFixed(2);

    function withSeparators(amount, decimals, thousands, decimal) {
      var fixed = (amount / 100).toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    }

    var amountStr;
    switch (token[1]) {
      case 'amount_no_decimals':
        amountStr = withSeparators(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amountStr = withSeparators(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amountStr = withSeparators(cents, 0, '.', ',');
        break;
      default:
        amountStr = withSeparators(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, amountStr);
  }

  function formatMoney(cents) {
    var format = getMoneyFormat();
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format);
    }
    return formatMoneyPipeline(cents, format);
  }

  function getDrawer() {
    return document.querySelector('#drawer-cart.smile-cart');
  }

  var DRAWER_OPEN_CLASS = 'drawer--visible';

  function isCartDrawerOpen() {
    var drawer = getDrawer();
    return !!(drawer && drawer.classList.contains(DRAWER_OPEN_CLASS));
  }

  function openCartDrawer() {
    var drawer = getDrawer();
    if (!drawer || isCartDrawerOpen()) return;
    drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
  }

  function setLineItemsShimmer(active) {
    var drawer = getDrawer();
    if (!drawer) return;
    var loader = drawer.querySelector('[data-cart-loading]');
    var items = drawer.querySelector('[data-line-items]');
    if (loader) loader.classList.toggle('is-shimmering', active);
    if (items) items.classList.toggle('is-shimmering', active);
    if (active && items && !items.querySelector('[data-cart-shimmer]')) {
      items.setAttribute('data-shimmer-restore', items.innerHTML);
    }
  }

  function setDrawerWideShimmer(active) {
    var drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.toggle('is-drawer-shimmering', active);
    drawer.classList.toggle('is-cart-updating', active);

    var loader = drawer.querySelector('[data-cart-loading]');
    if (loader) loader.classList.toggle('cart--loading', active);

    var promo = drawer.querySelector('[data-smile-cart-promo]');
    var promoShimmer = drawer.querySelector('[data-cart-promo-shimmer]');
    if (promoShimmer) promoShimmer.hidden = !active;
    if (active && promo) promo.classList.remove('cart--hidden');

    var footer = drawer.querySelector('[data-cart-bottom]');
    var footerShimmer = drawer.querySelector('[data-cart-footer-shimmer]');
    if (footerShimmer) footerShimmer.hidden = !active;
    if (active && footer) {
      footer.classList.remove('cart--hidden');
      footer.classList.add('is-shimmering');
    } else if (footer) {
      footer.classList.remove('is-shimmering');
    }

    var rec = drawer.querySelector('[data-smile-cart-recommendations]');
    var recShimmer = drawer.querySelector('[data-cart-rec-shimmer]');
    if (recShimmer) {
      if (active && rec) {
        rec.hidden = false;
        recShimmer.hidden = false;
      } else {
        recShimmer.hidden = true;
      }
    }

    var updatingShimmer = drawer.querySelector('[data-cart-updating-shimmer]');
    var shimmerTemplate = drawer.querySelector('[data-cart-shimmer-template]');
    var form = drawer.querySelector('[data-cart-form]');

    if (active) {
      var emptyBody =
        drawer.classList.contains('is-empty') ||
        (form && form.classList.contains('cart--hidden'));
      if (
        emptyBody &&
        updatingShimmer &&
        shimmerTemplate &&
        !updatingShimmer.querySelector('[data-cart-shimmer]')
      ) {
        updatingShimmer.innerHTML = shimmerTemplate.innerHTML;
        updatingShimmer.hidden = false;
      } else if (form && !form.classList.contains('cart--hidden')) {
        showShimmerSkeleton();
        setLineItemsShimmer(true);
      }
    } else {
      if (updatingShimmer) {
        updatingShimmer.innerHTML = '';
        updatingShimmer.hidden = true;
      }
      setLineItemsShimmer(false);
      var items = drawer.querySelector('[data-line-items]');
      if (items) items.removeAttribute('data-shimmer-restore');
    }
  }

  function prepareDrawerForCartUpdate() {
    setDrawerWideShimmer(true);
  }

  function finishDrawerLoadingState() {
    setDrawerWideShimmer(false);
  }

  function refreshDrawerAfterAdd(skipPrepare) {
    if (!skipPrepare) prepareDrawerForCartUpdate();

    return Promise.all([fetchCartJson(), fetchBundleSectionHtml()])
      .then(function (results) {
        var cart = results[0];
        applyBundleHtml(results[1], cart);
        return syncAutomaticDiscountsWork(cart);
      })
      .catch(function () {
        return fetchCartJson().then(function (cart) {
          var drawer = getDrawer();
          if (drawer) syncDrawerVisibility(drawer, cart);
          return cart;
        });
      })
      .finally(function () {
        finishDrawerLoadingState();
      });
  }

  function extractBundleParts(html) {
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var content = wrap.querySelector('[data-api-content]');
    if (!content) return null;
    var items = content.querySelector('[data-api-cart-items]');
    var subtotal = content.querySelector('[data-api-cart-subtotal]');
    return {
      itemsHtml: items ? items.innerHTML : '',
      subtotalHtml: subtotal ? subtotal.innerHTML : '',
    };
  }

  function cartHasItems(cart) {
    if (!cart) return false;
    if (typeof cart.item_count === 'number') return cart.item_count > 0;
    return !!(cart.items && cart.items.length > 0);
  }

  function emptyCart() {
    return { item_count: 0, items: [] };
  }

  function bundleHtmlHasLineItems(itemsHtml) {
    return !!(itemsHtml && itemsHtml.indexOf('data-cart-item') !== -1);
  }

  function applyBundleHtml(html, cart) {
    var drawer = getDrawer();
    if (!drawer) return false;

    var parts = extractBundleParts(html);
    if (!parts) return false;

    var items = drawer.querySelector('[data-line-items]');
    var subtotal = drawer.querySelector('[data-cart-subtotal]');
    var cartState = cartHasItems(cart) ? cart : emptyCart();

    if (!cartHasItems(cartState)) {
      finishDrawerLoadingState();
      if (items) {
        items.innerHTML = bundleHtmlHasLineItems(parts.itemsHtml)
          ? '<div class="smile-cart-items"></div>'
          : parts.itemsHtml || '<div class="smile-cart-items"></div>';
      }
      if (subtotal) subtotal.innerHTML = parts.subtotalHtml || '';
      syncDrawerVisibility(drawer, cartState);
      enhanceQuantityControls(drawer);
      enhanceRemoveLinks(drawer);
      setShimmer(false);
      return true;
    }

    if (!parts.itemsHtml) return false;

    if (items) items.innerHTML = parts.itemsHtml;
    if (subtotal) subtotal.innerHTML = parts.subtotalHtml;
    syncDrawerVisibility(drawer, cartState);

    enhanceQuantityControls(drawer);
    enhanceRemoveLinks(drawer);
    setShimmer(false);
    return true;
  }

  function fetchBundleSectionHtml() {
    return fetch(rootUrl + '?section_id=' + BUNDLE_SECTION, { credentials: 'same-origin' }).then(
      function (r) {
        return r.text();
      }
    );
  }

  function resetRecommendationsState() {
    lastRecommendationsProductId = 0;
  }

  function abortRecommendationsRequests() {
    recommendationsRequestId += 1;
  }

  function parseRecommendationsListHtml(html) {
    if (!html) return '';
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var content = wrap.querySelector('[data-smile-cart-recommendations-content]');
    var list = content && content.querySelector('.smile-cart__recommendations-list');
    return list && list.children.length > 0 ? list.innerHTML : '';
  }

  function fetchRecommendationsHtml(productId, intent, limit) {
    return fetch(buildRecommendationsUrl(productId, intent, limit), {
      credentials: 'same-origin',
    }).then(function (r) {
      return r.text();
    });
  }

  function buildRecommendationsUrl(productId, intent, limit) {
    var routes = window.theme && window.theme.routes;
    var prefix = (routes && routes.root_url) || '/';
    var path = (routes && routes.product_recommendations_url) || '/recommendations/products';
    if (prefix.slice(-1) !== '/') prefix += '/';
    if (path.charAt(0) === '/') path = path.slice(1);
    var qs =
      'section_id=' +
      RECOMMENDATIONS_SECTION +
      '&product_id=' +
      productId +
      '&limit=' +
      limit +
      '&intent=' +
      intent;
    return prefix + path + '?' + qs;
  }

  function getRecommendationProductId(cart) {
    if (!cart || !cart.items || !cart.items.length) return 0;
    var root = getRewardsRoot();
    var giftVariantId = getGiftVariantId(root);
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isConfiguredGiftLine(item, giftVariantId)) continue;
      return item.product_id;
    }
    return cart.items[0].product_id;
  }

  function applyRecommendationVariant(card, option) {
    if (!card || !option) return;

    var btn = card.querySelector('[data-smile-rec-add]');
    var priceEl = card.querySelector('[data-smile-rec-price]');
    var compareEl = card.querySelector('[data-smile-rec-compare]');
    var oosEl = card.querySelector('[data-smile-rec-oos]');
    var errorEl = card.querySelector('[data-smile-rec-error]');
    var img = card.querySelector('[data-smile-rec-image]');

    var price = Number(option.dataset.price || 0);
    var compare = Number(option.dataset.compare || 0);
    var available = option.dataset.available === 'true';

    if (btn) {
      btn.dataset.variantId = option.value;
      btn.disabled = !available;
    }

    if (priceEl) priceEl.textContent = formatMoney(price);

    if (compareEl) {
      if (compare > price) {
        compareEl.textContent = formatMoney(compare);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (oosEl) oosEl.hidden = available;

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    if (img && option.dataset.image) {
      img.src = option.dataset.image;
      if (option.dataset.imageAlt) img.alt = option.dataset.imageAlt;
    }
  }

  function addRecommendationVariantToCart(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return Promise.resolve();

    var card = btn.closest('[data-smile-rec-card]');
    var errorEl = card && card.querySelector('[data-smile-rec-error]');

    btn.disabled = true;
    btn.classList.add('loading');

    return fetch(cartUrl + '/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      })
      .then(function () {
        resetRecommendationsState();
        return fetch(cartUrl + '.js', { credentials: 'same-origin' }).then(function (r) {
          return r.json();
        });
      })
      .then(function () {
        return syncAutomaticDiscounts();
      })
      .then(function (finalCart) {
        publishCartChange(finalCart);
      })
      .catch(function (err) {
        if (errorEl) {
          errorEl.textContent =
            (err && err.description) || (err && err.message) || 'Could not add to cart';
          errorEl.hidden = false;
        }
      })
      .finally(function () {
        btn.classList.remove('loading');
        if (card) {
          var select = card.querySelector('[data-smile-rec-variant-select]');
          if (select) {
            applyRecommendationVariant(card, select.options[select.selectedIndex]);
          } else {
            btn.disabled = false;
          }
        }
      });
  }

  function onMixMatchAddedToCart() {
    resetRecommendationsState();
    prepareDrawerForCartUpdate();
    var drawer = getDrawer();
    if (drawer) drawer.dataset.smileSkipOpenSync = 'true';
    openCartDrawer();

    return refreshDrawerAfterAdd(true).then(function (finalCart) {
      publishCartChange(finalCart);
    });
  }

  function isFlavorPackForm(form) {
    return (
      form &&
      form.hasAttribute('data-product-form') &&
      !form.hasAttribute('data-smile-bundle-form') &&
      !!form.querySelector('input[name^="properties["]')
    );
  }

  function getPackLimitFromForm(form) {
    var selected = form.querySelector('.product_variant_button input[type="radio"]:checked');
    if (!selected) return 0;
    var match = selected.value.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  function getFlavorTotalFromForm(form) {
    var total = 0;
    form.querySelectorAll('input[name^="properties["]').forEach(function (input) {
      total += Number(input.value || 0);
    });
    return total;
  }

  function collectPackAddPayload(form) {
    var idInput = form.querySelector('input[name="id"]');
    var variantId = idInput && idInput.value;
    if (!variantId) return null;

    var properties = {};
    form.querySelectorAll('input[name^="properties["]').forEach(function (input) {
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = String(input.value || 0);
    });

    return {
      items: [{ id: Number(variantId), quantity: 1, properties: properties }],
    };
  }

  function collectBundleAddPayload(form) {
    var items = [];
    var packInput = form.querySelector("input[name='properties[Pack]']:checked");
    var packValue = packInput ? packInput.value : '';
    var packProps = packValue ? { Pack: packValue } : {};

    for (var i = 1; i <= 10; i++) {
      var idEl =
        form.querySelector('[name="items[' + i + '][id]"]') ||
        form.querySelector('#free-variant-id' + i);
      var qtyEl =
        form.querySelector('[name="items[' + i + '][quantity]"]') ||
        form.querySelector('#free-variant-qty' + i);
      if (!idEl || !qtyEl) continue;

      var id = idEl.value;
      var qty = Number(qtyEl.value || 0);
      if (!id || qty < 1) continue;

      items.push({
        id: Number(id),
        quantity: qty,
        properties: Object.assign({}, packProps),
      });
    }

    if (!items.length) return null;
    return { items: items };
  }

  function setPdpSubmitLoading(form, loading) {
    form.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      if (loading) {
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('loading');
      } else {
        btn.removeAttribute('disabled');
        btn.classList.remove('loading');
      }
    });
  }

  function afterPdpAddToCart() {
    prepareDrawerForCartUpdate();
    var drawer = getDrawer();
    if (drawer) drawer.dataset.smileSkipOpenSync = 'true';
    openCartDrawer();

    return refreshDrawerAfterAdd(true).then(function (finalCart) {
      publishCartChange(finalCart);
      return finalCart;
    });
  }

  function bindPdpProductForms() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        if (form.closest('#drawer-cart')) return;

        var isBundle = form.hasAttribute('data-smile-bundle-form');
        var isPack = isFlavorPackForm(form);
        if (!isBundle && !isPack) return;

        event.preventDefault();
        event.stopPropagation();

        if (isPack) {
          var limit = getPackLimitFromForm(form);
          var total = getFlavorTotalFromForm(form);
          if (limit < 1 || total !== limit) return;
        }

        var payload = isBundle ? collectBundleAddPayload(form) : collectPackAddPayload(form);
        if (!payload || !payload.items.length) return;

        setPdpSubmitLoading(form, true);

        fetch(cartUrl + '/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (j) { throw j; });
            return r.json();
          })
          .then(function () {
            return afterPdpAddToCart();
          })
          .catch(function (err) {
            var errEl = form.querySelector('[data-add-action-errors]');
            var msg =
              (err && err.description) || (err && err.message) || 'Could not add to cart';
            if (errEl) {
              errEl.innerHTML = '<div class="errors">' + msg + '</div>';
              errEl.hidden = false;
            } else {
              console.warn('[SmileCart] PDP add failed', err);
            }
          })
          .finally(function () {
            setPdpSubmitLoading(form, false);
          });
      },
      true
    );
  }

  function bindRecommendationCard(card) {
    if (!card || card.dataset.smileRecBound === 'true') return;
    card.dataset.smileRecBound = 'true';

    if (card.dataset.mixMatchSave === 'true') {
      if (window.SmileCartMixMatch) {
        window.SmileCartMixMatch.bindCard(card, onMixMatchAddedToCart);
      }
      return;
    }

    var select = card.querySelector('[data-smile-rec-variant-select]');
    var btn = card.querySelector('[data-smile-rec-add]');

    if (select) {
      select.addEventListener('change', function () {
        applyRecommendationVariant(card, select.options[select.selectedIndex]);
      });
      applyRecommendationVariant(card, select.options[select.selectedIndex]);
    }

    if (!btn) return;

    btn.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (btn.disabled || btn.classList.contains('loading')) return;
        addRecommendationVariantToCart(btn);
      },
      true
    );
  }

  function initRecommendationAddToCart(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-smile-rec-card]').forEach(bindRecommendationCard);
  }

  function setRecommendationsRecShimmer(container, active) {
    if (!container) return;
    var recShimmer = container.querySelector('[data-cart-rec-shimmer]');
    if (recShimmer) recShimmer.hidden = !active;
  }

  function renderRecommendationsOutput(container, output, listHtml) {
    setRecommendationsRecShimmer(container, false);
    if (listHtml) {
      output.innerHTML = listHtml;
      container.hidden = false;
      initRecommendationAddToCart(container);
      return true;
    }
    output.innerHTML = '';
    container.hidden = true;
    return false;
  }

  function loadCartRecommendations(cart) {
    var drawer = getDrawer();
    if (!drawer) return Promise.resolve();

    var container = drawer.querySelector('[data-smile-cart-recommendations]');
    if (!container) return Promise.resolve();

    if (!cartHasItems(cart)) {
      container.hidden = true;
      resetRecommendationsState();
      abortRecommendationsRequests();
      return Promise.resolve();
    }

    var productId = getRecommendationProductId(cart);
    if (!productId) {
      container.hidden = true;
      return Promise.resolve();
    }

    var intent = container.dataset.recommendationsIntent || 'complementary';
    var limit = container.dataset.recommendationsLimit || '3';
    var output = container.querySelector('[data-smile-cart-recommendations-output]');
    if (!output) return Promise.resolve();

    var hasRenderedProducts =
      productId === lastRecommendationsProductId &&
      output.querySelector('[data-smile-rec-card]');

    if (hasRenderedProducts) {
      container.hidden = false;
      initRecommendationAddToCart(container);
      return Promise.resolve();
    }

    lastRecommendationsProductId = productId;
    var requestId = ++recommendationsRequestId;
    container.hidden = false;
    output.innerHTML = '';
    var recShimmer = container.querySelector('[data-cart-rec-shimmer]');
    if (recShimmer) recShimmer.hidden = false;

    var primaryFetch = fetchRecommendationsHtml(productId, intent, limit);
    var fallbackFetch =
      intent === 'complementary'
        ? fetchRecommendationsHtml(productId, 'related', limit)
        : Promise.resolve('');

    return Promise.all([primaryFetch, fallbackFetch])
      .then(function (results) {
        if (requestId !== recommendationsRequestId) return;

        var listHtml = parseRecommendationsListHtml(results[0]);
        if (!listHtml && intent === 'complementary') {
          listHtml = parseRecommendationsListHtml(results[1]);
        }
        renderRecommendationsOutput(container, output, listHtml);
      })
      .catch(function () {
        if (requestId !== recommendationsRequestId) return;
        setRecommendationsRecShimmer(container, false);
        if (!output.querySelector('[data-smile-rec-card]')) {
          output.innerHTML = '';
          container.hidden = true;
        }
      });
  }

  function refreshCartRecommendationsFromServer() {
    return fetchCartJson()
      .then(function (cart) {
        if (!cartHasItems(cart)) return cart;
        return loadCartRecommendations(cart).then(function () {
          return cart;
        });
      })
      .catch(function () {});
  }

  function syncDrawerVisibility(drawer, cart) {
    var form = drawer.querySelector('[data-cart-form]');
    var bottom = drawer.querySelector('[data-cart-bottom]');
    var empty = drawer.querySelector('[data-cart-empty]');
    var promo = drawer.querySelector('[data-smile-cart-promo]');
    var loader = drawer.querySelector('[data-cart-loading]');
    var hasItems = cartHasItems(cart);

    drawer.classList.toggle('is-empty', !hasItems);

    if (hasItems) {
      if (form) form.classList.remove('cart--hidden');
      if (bottom) bottom.classList.remove('cart--hidden');
      if (empty) empty.classList.add('cart--hidden');
      if (promo) promo.classList.remove('cart--hidden');
    } else {
      if (form) form.classList.add('cart--hidden');
      if (bottom) bottom.classList.add('cart--hidden');
      if (empty) empty.classList.remove('cart--hidden');
      if (promo) promo.classList.add('cart--hidden');
    }
    if (loader) loader.classList.remove('cart--loading');

    if (hasItems && cart) {
      loadCartRecommendations(cart);
      updateCheckoutGiftState(cart);
    } else {
      var rec = drawer.querySelector('[data-smile-cart-recommendations]');
      if (rec) rec.hidden = true;
      resetRecommendationsState();
      abortRecommendationsRequests();
      updateCheckoutGiftState(cart || emptyCart());
    }
  }

  function setShimmer(active) {
    if (active) {
      setLineItemsShimmer(true);
      setFooterShimmer(true);
    } else {
      setLineItemsShimmer(false);
      setFooterShimmer(false);
    }
  }

  function showShimmerSkeleton() {
    var drawer = getDrawer();
    if (!drawer) return;
    var items = drawer.querySelector('[data-line-items]');
    if (!items || items.querySelector('[data-cart-shimmer]')) return;
    var shimmer = drawer.querySelector('[data-cart-shimmer-template]');
    if (shimmer) {
      items.innerHTML = shimmer.innerHTML;
    }
  }

  function setFooterShimmer(active) {
    var drawer = getDrawer();
    if (!drawer) return;
    var footer = drawer.querySelector('[data-cart-bottom]');
    if (footer) footer.classList.toggle('is-shimmering', active);
  }

  function setRowUpdating(row, active) {
    if (row) row.classList.toggle('is-updating', active);
  }

  /* ——— Progress bar rewards ——— */
  var checkIcon =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M6 10.2l2.4 2.4L14 7.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var emptyShippingIcon =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/></svg>';
  var emptyGiftIcon =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/><path d="M7 8h6l.8 7H6.2L7 8zm1.2-2h3.6l.6 2H7.6l.6-2z" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';

  function setMilestoneState(milestone, isComplete, emptyIcon) {
    if (!milestone) return;
    milestone.classList.toggle('is-complete', isComplete);
    var icon = milestone.querySelector('.smile-cart__milestone-icon');
    if (icon) icon.innerHTML = isComplete ? checkIcon : emptyIcon;
  }

  function getRewardsRoot() {
    return document.querySelector('#drawer-cart [data-smile-cart-rewards]');
  }

  /** Rewards block or drawer root — gift config is on both for reliable sync. */
  function getGiftConfigEl() {
    var drawer = getDrawer();
    var rewards = getRewardsRoot();
    if (rewards && rewards.dataset.giftVariantId) return rewards;
    if (drawer && drawer.dataset.giftVariantId) return drawer;
    return rewards || drawer;
  }

  function getGiftVariantId(root) {
    root = root || getGiftConfigEl();
    var id = root && root.dataset.giftVariantId;
    return id ? Number(id) : 0;
  }

  function getLineProperty(item, key) {
    if (!item || !item.properties) return '';
    var props = item.properties;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i] && props[i].name === key) return props[i].value;
      }
      return '';
    }
    return props[key] != null ? props[key] : '';
  }

  function isConfiguredGiftLine(item, giftVariantId) {
    if (!item || !giftVariantId) return false;
    return Number(item.variant_id) === Number(giftVariantId);
  }

  /** Line we added via /cart/add.js — distinct from the same variant bought at full price. */
  function cartHasAutoAddedGift(cart, giftVariantId) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return isConfiguredGiftLine(item, giftVariantId) && isAutoAddedGiftLine(item);
    });
  }

  function isAutoAddedGiftLine(item) {
    var value = getLineProperty(item, '_smile_auto_gift');
    return value === '1' || value === 1 || value === true;
  }

  /** Gift line added by us or discounted to free — safe to auto-remove when below threshold. */
  function isPromotionalFreeGiftLine(item, giftVariantId) {
    if (!isConfiguredGiftLine(item, giftVariantId)) return false;
    if (isAutoAddedGiftLine(item)) return true;
    return item.final_line_price === 0 || item.final_price === 0;
  }

  function getGiftMilestoneThresholdCents(root) {
    root = root || getGiftConfigEl();
    if (!root) return 0;
    var explicit = Number(root.dataset.giftThresholdCents || 0);
    if (explicit > 0) return explicit;
    var milestone = root.querySelector
      ? root.querySelector('[data-milestone][data-milestone-type="free_gift"]')
      : document.querySelector(
          '#drawer-cart [data-milestone][data-milestone-type="free_gift"]'
        );
    var fromMilestone = milestone ? Number(milestone.dataset.milestoneThreshold || 0) : 0;
    if (fromMilestone > 0) return fromMilestone;
    return Number(root.dataset.giftLimit || 0) * 100;
  }

  function findAutoGiftLineIndex(cart, giftVariantId) {
    if (!cart || !cart.items) return -1;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isConfiguredGiftLine(item, giftVariantId) && isAutoAddedGiftLine(item)) {
        return i + 1;
      }
    }
    return -1;
  }

  function getGiftDiscountTitle(cart, giftVariantId) {
    if (!cart || !cart.items) return '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!isConfiguredGiftLine(item, giftVariantId)) continue;
      var allocations = item.line_level_discount_allocations || [];
      for (var j = 0; j < allocations.length; j++) {
        var app = allocations[j].discount_application;
        if (app && app.title) return app.title;
      }
    }
    return '';
  }

  function getPaidSubtotalCents(cart, giftVariantId) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isPromotionalFreeGiftLine(item, giftVariantId)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function cartHasFreeGift(cart, giftVariantId) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return isPromotionalFreeGiftLine(item, giftVariantId);
    });
  }

  function getGiftLineTitle(cart, giftVariantId) {
    if (!cart || !cart.items) return '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isPromotionalFreeGiftLine(item, giftVariantId)) {
        return item.product_title || item.title || '';
      }
      if (isConfiguredGiftLine(item, giftVariantId)) {
        return item.product_title || item.title || '';
      }
    }
    return '';
  }

  function isMilestoneComplete(type, thresholdCents, cart, giftVariantId, paidSubtotal, total) {
    if (type === 'free_shipping') {
      var shippingLimit = thresholdCents;
      if (!shippingLimit) {
        shippingLimit = Number(getRewardsRoot().dataset.shippingLimit || 0) * 100;
      }
      return shippingLimit > 0 && total >= shippingLimit;
    }
    if (type === 'free_gift') {
      if (cartHasFreeGift(cart, giftVariantId)) return true;
      return thresholdCents > 0 && paidSubtotal >= thresholdCents;
    }
    if (type === 'custom') {
      return thresholdCents > 0 && paidSubtotal >= thresholdCents;
    }
    return false;
  }

  function milestoneEmptyIcon(type) {
    if (type === 'free_gift') return emptyGiftIcon;
    return emptyShippingIcon;
  }

  function updateSmileCartRewards(cart) {
    var root = getRewardsRoot();
    if (!cart) return;
    if (!root) return;

    var giftLimit = getGiftMilestoneThresholdCents(root);
    var giftVariantId = getGiftVariantId(root);
    var total = cart.total_price || 0;
    var paidSubtotal = getPaidSubtotalCents(cart, giftVariantId);
    var hasGift = cartHasFreeGift(cart, giftVariantId);
    var giftThresholdMet = giftLimit > 0 && paidSubtotal >= giftLimit;
    var maxCents = Number(root.dataset.progressMaxCents || 0);
    if (maxCents < 1) maxCents = 1;

    var percent = Math.min(100, (paidSubtotal / maxCents) * 100);
    if (giftLimit === 0 && maxCents > 0) {
      percent = Math.min(100, (total / maxCents) * 100);
    }

    var fill = root.querySelector('[data-smile-cart-progress-fill]');
    var thumb = root.querySelector('[data-smile-cart-progress-thumb]');
    var track = root.querySelector('[data-smile-cart-progress]');

    if (fill) fill.style.width = percent + '%';
    if (thumb) thumb.style.left = percent + '%';
    if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));

    root.querySelectorAll('[data-milestone]').forEach(function (milestone) {
      var type = milestone.dataset.milestoneType;
      var thresholdCents = Number(milestone.dataset.milestoneThreshold || 0);
      var complete = isMilestoneComplete(
        type,
        thresholdCents,
        cart,
        giftVariantId,
        paidSubtotal,
        total
      );
      setMilestoneState(milestone, complete, milestoneEmptyIcon(type));
    });

    var productTitle =
      getGiftLineTitle(cart, giftVariantId) || root.dataset.giftProductTitle || '';
    root.querySelectorAll('[data-milestone-label-dynamic]').forEach(function (labelEl) {
      if (productTitle) labelEl.textContent = productTitle;
    });

    if (root.dataset.showGiftProgress === 'true' && giftLimit > 0) {
      var progressMsg = root.querySelector('[data-gift-progress-message]');
      var successMsg = root.querySelector('[data-gift-success-message]');
      var template = root.dataset.giftMessageTemplate || '';
      var worth = root.dataset.giftWorth || '';
      var qualifiedText = root.dataset.giftQualifiedMessage || '';
      var discountTitle = getGiftDiscountTitle(cart, giftVariantId);

      if (successMsg) {
        successMsg.hidden = !hasGift;
        if (qualifiedText) successMsg.textContent = qualifiedText;
      }

      if (progressMsg && template) {
        if (hasGift || giftThresholdMet) {
          progressMsg.hidden = true;
        } else {
          var remaining = Math.max(0, giftLimit - paidSubtotal);
          var text = template
            .replace(/\|\|amount\|\|/g, formatMoney(remaining))
            .replace(/\|\|product\|\|/g, productTitle)
            .replace(/\|\|worth\|\|/g, worth)
            .replace(/\|\|discount\|\|/g, discountTitle);
          progressMsg.textContent = text;
          progressMsg.hidden = false;
          progressMsg.classList.remove('is-success');
        }
      }
    }
  }

  var giftAddPromise = null;
  var giftSyncChain = Promise.resolve();
  var giftCheckoutSyncPending = false;

  function shouldBlockCheckoutForGift(cart) {
    var root = getGiftConfigEl();
    var giftVariantId = getGiftVariantId(root);
    var giftLimitCents = getGiftMilestoneThresholdCents(root);
    if (!giftVariantId || giftLimitCents < 1) return false;
    if (!cart || !cartHasItems(cart)) return false;

    var hasAutoGift = cartHasAutoAddedGift(cart, giftVariantId);
    var paidSubtotal = getPaidSubtotalCents(cart, giftVariantId);

    if (paidSubtotal >= giftLimitCents && !hasAutoGift) return true;
    if (paidSubtotal < giftLimitCents && hasAutoGift) return true;
    return false;
  }

  function updateCheckoutGiftState(cart, options) {
    var drawer = getDrawer();
    if (!drawer) return;

    var btn = drawer.querySelector('[data-smile-checkout-btn], #checkout2');
    if (!btn) return;

    var syncing = (options && options.syncing) || giftCheckoutSyncPending;
    var block =
      !cartHasItems(cart) || syncing || shouldBlockCheckoutForGift(cart);

    btn.disabled = block;
    btn.classList.toggle('is-disabled', block);
    btn.setAttribute('aria-disabled', block ? 'true' : 'false');
  }

  function fetchCartJson() {
    return fetch(cartUrl + '.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function addConfiguredGiftToCart(giftVariantId) {
    if (!giftVariantId) return Promise.resolve(null);
    if (giftAddPromise) return giftAddPromise;

    giftAddPromise = fetch(cartUrl + '/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: giftVariantId,
            quantity: 1,
            properties: { _smile_auto_gift: '1' },
          },
        ],
      }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      })
      .then(function () {
        return fetchCartJson();
      })
      .catch(function (err) {
        console.warn('[SmileCart] Could not add free gift', err);
        return null;
      })
      .finally(function () {
        giftAddPromise = null;
      });

    return giftAddPromise;
  }

  function removeConfiguredGiftFromCart(cart, giftVariantId) {
    var line = findAutoGiftLineIndex(cart, giftVariantId);
    if (line < 1) return Promise.resolve(cart);

    return fetch(cartUrl + '/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: String(line),
        quantity: 0,
        sections: BUNDLE_SECTION,
        sections_url: '/cart',
      }),
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      })
      .then(function (data) {
        applySectionsFromChangeResponse(data);
        return data;
      });
  }

  function applySectionsFromChangeResponse(data) {
    if (!data) return;
    var bundleHtml = data.sections && data.sections[BUNDLE_SECTION];
    var cartState = cartHasItems(data) ? data : emptyCart();
    if (bundleHtml) {
      applyBundleHtml(bundleHtml, cartState);
    } else if (!cartHasItems(cartState)) {
      var drawer = getDrawer();
      if (drawer) syncDrawerVisibility(drawer, cartState);
    }
    updateSmileCartRewards(cartState);
  }

  function refreshBundleFromServer(cart) {
    var cartState = cartHasItems(cart) ? cart : emptyCart();
    return fetchBundleSectionHtml().then(function (html) {
      applyBundleHtml(html, cartState);
      updateSmileCartRewards(cartState);
      return cartState;
    });
  }

  function runGiftSync() {
    return syncAutomaticDiscounts();
  }

  function syncAutomaticDiscounts() {
    giftCheckoutSyncPending = true;

    var run = function () {
      return fetchCartJson().then(function (cart) {
        updateCheckoutGiftState(cart, { syncing: true });
        return syncAutomaticDiscountsWork(cart);
      });
    };

    var result = giftSyncChain.then(run, run);
    giftSyncChain = result.then(
      function () {},
      function () {}
    );
    result.finally(function () {
      giftCheckoutSyncPending = false;
      fetchCartJson()
        .then(function (cart) {
          updateCheckoutGiftState(cart, { syncing: false });
        })
        .catch(function () {
          updateCheckoutGiftState(emptyCart(), { syncing: false });
        });
    });
    return result;
  }

  function syncAutomaticDiscountsWork(cart) {
    if (!cart || !cart.items) return Promise.resolve(cart);

    var root = getGiftConfigEl();
    var giftVariantId = getGiftVariantId(root);
    var giftLimitCents = getGiftMilestoneThresholdCents(root);

    if (!giftVariantId || giftLimitCents < 1) {
      return refreshBundleFromServer(cart);
    }

    var hasAutoGift = cartHasAutoAddedGift(cart, giftVariantId);
    var paidSubtotal = getPaidSubtotalCents(cart, giftVariantId);
    var needsGift = paidSubtotal >= giftLimitCents && !hasAutoGift;
    var shouldRemoveGift = hasAutoGift && paidSubtotal < giftLimitCents;

    if (shouldRemoveGift) {
      return removeConfiguredGiftFromCart(cart, giftVariantId).then(function (data) {
        var nextCart = data && cartHasItems(data) ? data : cart;
        return refreshBundleFromServer(nextCart);
      });
    }

    if (needsGift) {
      return addConfiguredGiftToCart(giftVariantId).then(function (fresh) {
        if (fresh && cartHasAutoAddedGift(fresh, giftVariantId)) {
          return refreshBundleFromServer(fresh);
        }
        return fetchCartJson().then(function (latest) {
          return refreshBundleFromServer(latest || cart);
        });
      });
    }

    return refreshBundleFromServer(cart);
  }

  function publishCartChange(cart) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: { cart: cart, smileInternal: true },
        bubbles: true,
      })
    );
  }

  var cartSyncInFlight = false;
  var cartSyncQueued = false;

  function findLineIndex(cart, lineKey) {
    if (!cart || !cart.items) return -1;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === lineKey) return i + 1;
    }
    return -1;
  }

  function changeLine(lineKey, quantity, row) {
    setRowUpdating(row, true);
    setFooterShimmer(true);

    return fetch(cartUrl + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var line = findLineIndex(cart, lineKey);
        if (line < 1) throw new Error('Line not found');
        return fetch(cartUrl + '/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: String(line),
            quantity: quantity,
            sections: BUNDLE_SECTION,
            sections_url: '/cart',
          }),
        });
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      })
      .then(function (data) {
        applySectionsFromChangeResponse(data);
        return syncAutomaticDiscounts();
      })
      .then(function (finalCart) {
        publishCartChange(finalCart);
        return finalCart;
      })
      .finally(function () {
        setRowUpdating(row, false);
        setFooterShimmer(false);
        setShimmer(false);
      });
  }

  var debounceTimers = new Map();

  function scheduleQuantityChange(lineKey, quantity, row) {
    var timerKey = lineKey || (row && row.dataset.cartItemKey);
    if (!timerKey) return;
    var prev = debounceTimers.get(timerKey);
    if (prev) clearTimeout(prev);
    debounceTimers.set(
      timerKey,
      setTimeout(function () {
        changeLine(lineKey, quantity, row);
      }, DEBOUNCE_MS)
    );
  }

  function readQuantityValue(input, min) {
    var qty = parseInt(input.value, 10);
    if (isNaN(qty)) qty = 0;
    return Math.max(min, qty);
  }

  function enhanceQuantityControls(scope) {
    scope.querySelectorAll('[data-smile-qty]').forEach(function (wrapper) {
      if (wrapper.dataset.smileQtyBound === 'true') return;
      wrapper.dataset.smileQtyBound = 'true';

      var input = wrapper.querySelector('[data-quantity-input]');
      var decrease = wrapper.querySelector('[data-decrease-quantity]');
      var increase = wrapper.querySelector('[data-increase-quantity]');
      if (!input) return;

      var lineKey = input.getAttribute('data-smile-line-key');
      var row = wrapper.closest('[data-cart-item]');
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 0;

      function commitQuantity() {
        var qty = readQuantityValue(input, min);
        input.value = qty;
        scheduleQuantityChange(lineKey, qty, row);
      }

      if (decrease) {
        decrease.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();
            var qty = readQuantityValue(input, min);
            input.value = Math.max(min, qty - 1);
            commitQuantity();
          },
          true
        );
      }

      if (increase) {
        increase.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();
            var qty = readQuantityValue(input, min);
            input.value = qty + 1;
            commitQuantity();
          },
          true
        );
      }

      input.addEventListener('change', function (e) {
        e.stopImmediatePropagation();
        commitQuantity();
      });
    });
  }

  function enhanceRemoveLinks(scope) {
    scope.querySelectorAll('[data-remove-key]').forEach(function (link) {
      if (link.dataset.smileRemoveEnhanced) return;
      link.dataset.smileRemoveEnhanced = 'true';

      link.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          var key = link.getAttribute('data-remove-key');
          var row = link.closest('[data-cart-item]');
          changeLine(key, 0, row);
        },
        true
      );
    });
  }

  function enhanceDrawerInteractions(drawer) {
    enhanceQuantityControls(drawer);
    enhanceRemoveLinks(drawer);

    var items = drawer.querySelector('[data-line-items]');
    if (!items || items.dataset.smileObserve) return;
    items.dataset.smileObserve = 'true';

    var observer = new MutationObserver(function () {
      enhanceQuantityControls(drawer);
      enhanceRemoveLinks(drawer);
    });
    observer.observe(items, { childList: true, subtree: true });
  }

  function clearBannerAutoscroll(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-smile-banner-autoscroll]').forEach(function (wrap) {
      if (wrap._smileBannerTimer) {
        clearInterval(wrap._smileBannerTimer);
        wrap._smileBannerTimer = null;
      }
      wrap._smileBannerPaused = false;
      wrap._smileBannerIndex = 0;
    });
  }

  function bindBannerPauseHandlers(wrap) {
    if (wrap.dataset.smileBannerPauseBound === 'true') return;
    wrap.dataset.smileBannerPauseBound = 'true';

    wrap.addEventListener('mouseenter', function () {
      wrap._smileBannerPaused = true;
    });
    wrap.addEventListener('mouseleave', function () {
      wrap._smileBannerPaused = false;
    });
    wrap.addEventListener(
      'touchstart',
      function () {
        wrap._smileBannerPaused = true;
      },
      { passive: true }
    );
    wrap.addEventListener(
      'touchend',
      function () {
        window.setTimeout(function () {
          wrap._smileBannerPaused = false;
        }, 4000);
      },
      { passive: true }
    );
    wrap.addEventListener('focusin', function () {
      wrap._smileBannerPaused = true;
    });
    wrap.addEventListener('focusout', function () {
      wrap._smileBannerPaused = false;
    });
  }

  function initBannerAutoscroll(scope) {
    if (!scope) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    clearBannerAutoscroll(scope);

    scope.querySelectorAll('[data-smile-banner-autoscroll]').forEach(function (wrap) {
      var track = wrap.querySelector('.smile-cart__banner-track');
      if (!track) return;

      var slides = track.querySelectorAll('.smile-cart__banner-slide');
      if (slides.length < 2) return;

      var interval = Number(wrap.dataset.smileBannerInterval) || 5000;
      wrap._smileBannerPaused = false;
      wrap._smileBannerIndex = 0;

      bindBannerPauseHandlers(wrap);

      wrap._smileBannerTimer = window.setInterval(function () {
        if (!wrap.isConnected || wrap._smileBannerPaused) return;
        wrap._smileBannerIndex =
          (wrap._smileBannerIndex + 1) % slides.length;
        var slide = slides[wrap._smileBannerIndex];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }, interval);
    });
  }

  function bindDrawer() {
    var drawer = getDrawer();
    if (!drawer || drawer.dataset.smileCartBound) return;
    drawer.dataset.smileCartBound = 'true';

    enhanceDrawerInteractions(drawer);

    drawer.addEventListener('theme:drawer:open', function () {
      if (drawer.dataset.smileSkipOpenSync === 'true') {
        drawer.dataset.smileSkipOpenSync = '';
        initBannerAutoscroll(drawer);
        return;
      }

      setDrawerWideShimmer(true);

      runGiftSync()
        .then(function (finalCart) {
          if (finalCart) updateSmileCartRewards(finalCart);
        })
        .catch(function () {})
        .finally(function () {
          finishDrawerLoadingState();
        });

      initBannerAutoscroll(drawer);
    });

    drawer.addEventListener('theme:drawer:close', function () {
      clearBannerAutoscroll(drawer);
    });

    var loader = drawer.querySelector('[data-cart-loading]');
    if (loader) {
      new MutationObserver(function () {
        var loading = loader.classList.contains('cart--loading');
        setShimmer(loading);
        if (loading) showShimmerSkeleton();
      }).observe(loader, { attributes: true, attributeFilter: ['class'] });
    }
  }

  document.addEventListener('theme:cart:popdown', function () {
    openCartDrawer();
  });

  function runCartSyncFromTheme() {
    if (cartSyncInFlight) {
      cartSyncQueued = true;
      return Promise.resolve();
    }

    cartSyncInFlight = true;
    return syncAutomaticDiscounts()
      .then(function (finalCart) {
        publishCartChange(finalCart);
        return finalCart;
      })
      .catch(function () {})
      .finally(function () {
        cartSyncInFlight = false;
        if (cartSyncQueued) {
          cartSyncQueued = false;
          runCartSyncFromTheme();
        }
      });
  }

  document.addEventListener('theme:cart:change', function (event) {
    if (event.detail && event.detail.smileInternal) {
      var cart = event.detail.cart;
      if (cart) {
        updateSmileCartRewards(cart);
        if (!cartHasItems(cart)) {
          var drawer = getDrawer();
          if (drawer) syncDrawerVisibility(drawer, emptyCart());
        }
      }
      return;
    }

    runCartSyncFromTheme();
  });

  document.addEventListener('theme:cart:reload', function () {
    resetRecommendationsState();
    runCartSyncFromTheme();
  });

  function bindDrawerRecommendationsPrefetch() {
    var drawer = getDrawer();
    if (!drawer || drawer.dataset.smileRecPrefetchBound === 'true') return;
    drawer.dataset.smileRecPrefetchBound = 'true';

    drawer.addEventListener('theme:drawer:open', function () {
      if (!drawer.querySelector('[data-smile-cart-recommendations]')) return;
      refreshCartRecommendationsFromServer();
    });
  }

  function bindAddToCartDrawerOpen() {
    document.addEventListener(
      'click',
      function (event) {
        var btn = event.target.closest('.btn--add-to-cart');
        if (!btn || btn.disabled) return;
        if (btn.closest('#drawer-cart')) return;
        if (btn.closest('[data-smile-rec-add]')) return;

        var form = btn.closest('form');
        if (
          form &&
          (form.hasAttribute('data-smile-bundle-form') || isFlavorPackForm(form))
        ) {
          return;
        }

        openCartDrawer();
      },
      true
    );
  }

  function init() {
    bindDrawer();
    bindDrawerRecommendationsPrefetch();
    bindPdpProductForms();
    bindAddToCartDrawerOpen();
    var drawer = getDrawer();
    if (drawer && isCartDrawerOpen()) {
      initBannerAutoscroll(drawer);
    }
  }

  window.SmileCart = window.SmileCart || {};
  window.SmileCart.formatMoney = formatMoney;
  window.SmileCart.openDrawer = openCartDrawer;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
